// Banner mounted once in Layout (KAN-225) so every page offers "resume" when the renter
// has an unfinished checkout draft. Hidden during checkout, sign-in and QR inspection.
// Clicking "Resume" deep-links back to /book/:id?start=...&end=... with selections preserved.

import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { formatTripRange } from "../lib/dateLocal";
import { ArrowRight, Bookmark, X } from "lucide-react";

// Routes where the banner would be redundant or disruptive: checkout (/book/:id and
// /book/:id/complete), sign-in, and the mobile QR inspection flow.
const HIDDEN_PREFIXES = ["/book/", "/login", "/inspect/"];

export function shouldHideResumeDraft(pathname) {
  const p = pathname || "";
  return HIDDEN_PREFIXES.some((prefix) => p === prefix.replace(/\/$/, "") || p.startsWith(prefix));
}

export default function ResumeDraftBanner() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const hidden = shouldHideResumeDraft(pathname);
  const [draft, setDraft] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Re-fetch when entering a visible page (e.g. leaving checkout) so a fresh draft shows up.
  useEffect(() => {
    if (!user) { setDraft(null); return; }
    if (hidden) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/bookings/draft/me`, { withCredentials: true });
        if (alive) { setDraft(r.data || null); setDismissed(false); }
      } catch (e) { if (alive) setDraft(null); }
    })();
    return () => { alive = false; };
  }, [user, hidden]);

  const dismiss = async () => {
    setDismissed(true);
    try { await axios.delete(`${API}/bookings/draft`, { withCredentials: true }); }
    catch (e) {}
  };

  if (!user || hidden || !draft || dismissed) return null;
  const { vehicle_id, start_date, end_date, vehicle_snapshot: v = {} } = draft;
  const target = `/book/${vehicle_id}?start=${encodeURIComponent(start_date || "")}&end=${encodeURIComponent(end_date || "")}`;

  return (
    <div
      className="border-b border-[var(--zc-border)]"
      style={{ background: "var(--zc-accent-soft)" }}
      data-testid="resume-draft-banner"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-3 flex items-center gap-3">
        <span className="w-8 h-8 rounded-full grid place-items-center bg-white/70 text-[var(--zc-accent)] shrink-0">
          <Bookmark size={14} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-[var(--zc-accent)]">Pick up where you left off</div>
          <div className="text-sm text-[var(--zc-text)] truncate mt-0.5">
            {v.year} {v.make} {v.model} · {formatTripRange(start_date, end_date) || "—"}
          </div>
        </div>
        <Link
          to={target}
          className="zc-btn-primary px-4 h-9 text-xs flex items-center gap-1.5 whitespace-nowrap"
          data-testid="resume-draft-cta"
        >
          Resume <ArrowRight size={12} />
        </Link>
        <button
          onClick={dismiss}
          className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/50 transition shrink-0"
          aria-label="Dismiss"
          data-testid="resume-draft-dismiss"
        >
          <X size={14} className="text-[var(--zc-text-2)]" />
        </button>
      </div>
    </div>
  );
}
